import type { NavItemConfig } from "@/types/nav";

export function isNavItemActive({
	disabled,
	external,
	href,
	matcher,
	pathname,
	except,
}: Pick<NavItemConfig, "disabled" | "external" | "href" | "matcher"> & { pathname: string; except?: string[] }): boolean {
	if (disabled || !href || external) {
		return false;
	}

	if (matcher) {
		if (matcher.type === "startsWith") {
			return pathname.startsWith(matcher.href);
		}

		if (matcher.type === "equals") {
			return pathname === matcher.href;
		}

		return false;
	}

  const pathItems = pathname.split("/");
  const hrefItems = href.split("/");

  if (except?.some(b => pathname.startsWith(b))) {
    return false;
  }

  if (hrefItems.length > 2) {
		return pathItems.slice(0, 3).join("/") === hrefItems.slice(0, 3).join("/");
	}

	return pathItems.slice(0, 2).join("/") === hrefItems.slice(0, 2).join("/");
}

/**
 * Whether anything below this branch is the page you are on.
 *
 * Only the immediate children used to be checked, which was true enough while branches held
 * links. Admin Settings holds Plugins, so on `/plugins/services` the match sits two levels down
 * and the group would render collapsed with no trace of where you are.
 */
export function containsActiveHref(items: NavItemConfig[], pathname: string): boolean {
	return items.some(
		(item) => (item.href && pathname.startsWith(item.href)) || containsActiveHref(item.items ?? [], pathname)
	);
}

/**
 * Whether this entry is worth drawing for this viewer.
 *
 * A branch is drawn only when something under it is: Admin Settings holds Plugins, which needs
 * a workspace and the plugin permission, so an administrator without either would otherwise be
 * shown a group that opens onto nothing.
 */
export function isNavItemVisible(
	item: NavItemConfig,
	viewer: { hasAccount: boolean; isSuperAdmin: boolean; permissions: Record<string, unknown> }
): boolean {
	if (item.needAccount && !viewer.hasAccount) return false;
	if (item.isSuperAdmin && !viewer.isSuperAdmin) return false;
	if (item.permission && !viewer.permissions[item.permission]) return false;
	if (item.items?.length) return item.items.some((child) => isNavItemVisible(child, viewer));
	return true;
}
